/**
 * Display title of a track: the name plus its version in parentheses —
 * "Fading Light (Extended Mix)", "Fading Light (Jilax Remix)".
 *
 * The version lives in `Track.assetVersion` (FUGA `asset_version`). It is
 * appended everywhere a track title is shown, EXCEPT for the original cut:
 * "Original Mix" (and its spellings) is the default and stays silent —
 * Benjamin, 2026-09-08: "Die Version eines Tracks muss unbedingt in
 * Klammern angezeigt werden wenn sie nicht Original Mix ist."
 *
 * Shared by server (statements, invoices, download filenames, feed) and
 * client (every list, card and player title). `name` itself stays the raw
 * FUGA title so editing and delivery are untouched.
 */
export interface TrackTitleSource {
    name?: string | null
    assetVersion?: string | null
}

const ORIGINAL_VERSIONS = new Set(['', 'original', 'original mix', 'original version', 'orig', 'orig mix', 'original edit'])

/** True for the default cut, whose version is never displayed. */
export function isOriginalMixVersion(version: string | null | undefined): boolean {
    return ORIGINAL_VERSIONS.has(String(version || '').trim().toLowerCase())
}

/** The version to show for a track, or `null` when it is the original mix. */
export function trackVersionLabel(track: TrackTitleSource | null | undefined): string | null {
    const v = String(track?.assetVersion || '').trim()
    if (!v || isOriginalMixVersion(v)) return null
    return v
}

/** `name (version)` — or just the name for the original mix / no version. */
export function trackDisplayTitle(track: TrackTitleSource | null | undefined): string {
    const name = String(track?.name || '').trim()
    const version = trackVersionLabel(track)
    if (!version) return name
    // Some catalogue titles already carry the version ("Song (Radio Edit)")
    // — don't print it twice.
    if (name.toLowerCase().includes(`(${version.toLowerCase()})`)) return name
    return name ? `${name} (${version})` : version
}
